var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _class, _class2;

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }

/**
 * @copyright
 * Copyright(c) 2020 SICK AG
 */
import { Component, Prop, Watch } from '@sick-davinci/ce-decorators';
import { createValuesGridStyle } from '../_utils/GridTools.js';
import { ValueRangeScale } from '../_utils/ValueRange.js';
import { ValueRangeTools } from '../_utils/ValueRangeTools.js';
import { ThemeService } from '../_utils/ThemeService.js';
import { AbstractChart } from '../_utils/AbstractChart.js';
/**
 * A base class for bar charts
 */

export let AbstractBarChart = (_dec = Component({
  abstract: true
}), _dec2 = Reflect.metadata("design:paramtypes", [Object]), _dec3 = Prop(), _dec4 = Reflect.metadata("design:type", Boolean), _dec5 = Watch('palette'), _dec6 = Reflect.metadata("design:paramtypes", []), _dec(_class = _dec2(_class = (_class2 = class AbstractBarChart extends AbstractChart {
  /**
   * The values; the first dimension denotes the index of the category, the second dimension the index of the bar/section; that is, value[2][4] stores the value of the 5th bar/section within the 3rd category
   */

  /**
   * Whether the bar chart is horizontal (true) or vertical (false)
   */
  constructor(seriesColors) {
    super();

    _defineProperty(this, "_values", []);

    _defineProperty(this, "_horizontal", false);

    if (seriesColors) {
      this._setSeriesColors(seriesColors);
    }
  }
  /**
   * Whether the bar chart is horizontal (true) or vertical (false)
   *
   * @default false
   */


  get horizontal() {
    return this._horizontal;
  }

  set horizontal(value) {
    this._horizontal = value;

    this._update();
  }
  /**
   * Sets the values to be used for the bars.
   *
   * Beware that the order of values should reflect the order of colors if the 'state' color palette is active.
   *
   * @param values - a two dimensional array; the first dimension denotes the index of the group/bar, the second dimension the index of the bar/segment; that is, value[2][4] stores the value of the 5th bar/segment of the 3rd group/bar
   */


  _setValues(values) {
    // map Infinity, -Infinity to 0
    this._values = values.map(arr => arr.map(value => value === Number.POSITIVE_INFINITY || value === Number.NEGATIVE_INFINITY ? 0.0 : value));

    this._update();
  }
  /**
   * Gets the values to be used for the bars.
   *
   * @returns a two dimensional array; the first dimension denotes the index of the group/bar, the second dimension the index of the bar/segment; that is, value[2][4] stores the value of the 5th bar/segment of the 3rd group/bar
   */


  _getValues() {
    return this._values;
  }
  /**
   * recycle holder the holder: needed, since holds the actual chart as a child
   */


  _getHolder() {
    var _this$shadowRoot;

    let holder = (_this$shadowRoot = this.shadowRoot) === null || _this$shadowRoot === void 0 ? void 0 : _this$shadowRoot.getElementById('holder');

    if (!holder) {
      holder = document.createElement('div');
      holder.setAttribute('id', 'holder');
    }

    return holder;
  }

  paletteWatcher() {
    this._update();
  }

  _setSeriesColors(colors) {
    super._setSeriesColors(colors);

    this._update();
  }

  componentConnected() {
    super.componentConnected();
    ThemeService.GET_INSTANCE().addListener(this._themeListener);
  }

  componentDisconnected() {
    super.componentDisconnected();
    ThemeService.GET_INSTANCE().removeListener(this._themeListener);
  }
  /**
   * Updates bar chart
   */


  /**
   * Ensures that every category has the same number of values.
   * Missing values are filled in at the end of the values-array.
   * If the values-array is undefined it will remain undefined.
   *
   * @private
   */
  _sanitizeCategoryValues(categories) {
    const maxValuesCount = categories.map(category => {
      var _category$values;

      return ((_category$values = category.values) === null || _category$values === void 0 ? void 0 : _category$values.length) || 0;
    }).reduce((previousValuesCount, currentValuesCount) => Math.max(previousValuesCount, currentValuesCount), 0);
    categories.forEach(category => {
      if (category.values !== undefined) {
        while (category.values.length < maxValuesCount) {
          category.values.push(0.0);
        }
      }
    });
  }

  _createData() {
    const barChartData = {};
    {
      const labels = this._getCategoryLabels();

      const bars = this._ensureMinBarCount(labels.length, barChartData);

      for (let i = 0; i < labels.length; i++) {
        bars[i].label = labels[i];
      }
    }
    {
      const bars = this._ensureMinBarCount(this._categoryIcons.length, barChartData);

      for (let i = 0; i < this._categoryIcons.length; i++) {
        bars[i].image = this._categoryIcons[i];
      }
    }
    {
      const bars = this._ensureMinBarCount(this._values.length, barChartData);

      for (let i = 0; i < this._values.length; i++) {
        bars[i].values = this._values[i];
      }
    }

    this._sanitizeCategoryValues(barChartData.categories);

    return barChartData;
  }

  *_iterator() {
    for (const barValues of this._values) {
      for (const value of barValues) {
        yield value;
      }
    }
  }

  _computeBarValueRange() {
    const computedValueRange = ValueRangeTools.COMPUTE_VALUE_RANGE(this._iterator(), this.valueRange);
    return {
      start: computedValueRange.start,
      end: computedValueRange.end,
      scale: computedValueRange.scale === ValueRangeScale.linear ? 'Linear' : 'Log10'
    };
  }
  /**
   * Ensures that the given data object contains at least the given number of elements.
   * The data object will be filled up with empty objects ({}) if necessary.
   *
   * @param minBarCount - the target minimum bar count.
   * @param barChartData - the data object to modify
   * @returns a reference to the array of bars of the given data object
   */


  _ensureMinBarCount(minBarCount, barChartData) {
    if (!barChartData.categories) {
      barChartData.categories = [];
    }

    while (barChartData.categories.length < minBarCount) {
      barChartData.categories.push({});
    }

    return barChartData.categories;
  }
  /**
   * Creates a new style object that should be filled with the specific properties.
   * @param hostFontFamily
   */


  _createStyle(hostFontFamily) {
    const style = {
      horizontal: this._horizontal
    };
    style.valueRange = this._computeBarValueRange();
    style.categoryAxisStyle = this._computeCategoryAxisStyle(this.horizontal, hostFontFamily);
    style.gridStyle = createValuesGridStyle('value', style.valueRange);
    return style;
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "horizontal", [_dec3, _dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "horizontal"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "paletteWatcher", [_dec5, _dec6], Object.getOwnPropertyDescriptor(_class2.prototype, "paletteWatcher"), _class2.prototype)), _class2)) || _class) || _class);
